import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { DateTime, Schema } from 'effect';
import { Effect, Request, RequestResolver } from 'effect';

import type {
  ApiInfrastructureErrors,
  BackendApiErrorUnion,
} from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { liftRequest } from '../context';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '../../../service/auth/AuthToken';
import type { LoggerConfig } from '../../../service/logger/LoggerConfig';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type { HydraCollectionResponseType } from '@vs-shop/schemas/openapi/response';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { HttpClient } from 'effect/unstable/http/HttpClient';
import { matchStatus } from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/brands';
const HTTP_METHOD = 'get';

export type MakeListBrandsDeps<BrandIndex extends Schema.Top> = {
  readonly brandIndex: BrandIndex;
};

export const makeListBrands = <BrandIndex extends Schema.Top>(
  deps: MakeListBrandsDeps<BrandIndex>
) => {
  const ListBrandsError = createBackendApiError({
    domain: 'Brand',
    endpoint: 'List',
    path: ENDPOINT,
    identifierField: 'page',
  });

  type GetBrandsError = BackendApiErrorUnion<typeof ListBrandsError>;

  const getBrandsFetch = (
    alphabet: string | null,
    name: string | null,
    page: number,
    itemsPerPage: number
  ) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({
          query: {
            page: page,
            itemsPerPage,
            visibility: 'yes',
            ...(alphabet !== null ? { alphabet } : {}),
            ...(name !== null ? { name } : {}),
          },
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'brands.page': page,
          'brands.itemsPerPage': itemsPerPage,
          ...(alphabet !== null ? { 'brands.alphabet': alphabet } : {}),
          ...(name !== null ? { 'brands.name': name } : {}),
        },
      })
    );

  const getBrands = (
    alphabet: string | null,
    name: string | null,
    page: number,
    itemsPerPage: number
  ) =>
    getBrandsFetch(alphabet, name, page, itemsPerPage).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.brandIndex))(response),
          404: (response) => Effect.fail(new ListBrandsError.NotFoundError({ page, response })),
          orElse: (response) =>
            Effect.fail(new ListBrandsError.ApiRequestError({ page, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.BrandList,
          'Debug',
          `Successfully resolved ${String(result['hydra:member'].length)} brands${alphabet !== null ? ` for alphabet: ${alphabet}` : ''}${name !== null ? ` with name filter: ${name}` : ''}`
        )
      ),
      Effect.withSpan('api.brand.list', {
        kind: 'internal',
        attributes: {
          'brands.page': page,
          'brands.itemsPerPage': itemsPerPage,
          ...(alphabet !== null ? { 'brands.alphabet': alphabet } : {}),
          ...(name !== null ? { 'brands.name': name } : {}),
        },
      })
    );

  type GetBrandsRequest = {
    readonly _tag: 'GetBrandsRequest';
    readonly alphabet: string | null;
    readonly name: string | null;
    readonly page: number;
    readonly itemsPerPage: number;
  } & Request.Request<
    HydraCollectionResponseType<BrandIndex>,
    GetBrandsError | ApiInfrastructureErrors,
    BackendApi | AuthToken | HttpClient | LoggerConfig | DateTime.CurrentTimeZone
  >;

  const GetBrandsRequest = Request.tagged<GetBrandsRequest>('GetBrandsRequest');

  const GetBrandsResolver = RequestResolver.fromEffect(
    liftRequest((request: GetBrandsRequest) =>
      getBrands(request.alphabet, request.name, request.page, request.itemsPerPage)
    )
  );

  const requestBrands = (
    alphabet: string | null,
    name: string | null,
    page: number,
    itemsPerPage: number
  ) => Effect.request(GetBrandsRequest({ alphabet, name, page, itemsPerPage }), GetBrandsResolver);

  return { getBrands, requestBrands, ListBrandsError };
};
